import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { UncontrolledPopover } from 'reactstrap'
import PropTypes from 'prop-types'

import * as constants from '../../misc/Constants'
import SeriesRow from './SeriesRow'
import Placeholder from '../common/Placeholder'
import Loader from '../common/loader/legacy-loaders/Op36Loader-web'
import Pagination from '../common/Pagination'
import SeriesEventRow from './SeriesEventRow'
import colors from '../../assets/styles/globals.scss'

import styles from './SeriesList.module.scss'
import { ReactComponent as GolferIcon } from '../../assets/images/common/icons/golfer-half-icon.svg'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import CustomPlaceholder from '../common/CustomPlaceholder'

const COMPLETED_SERIES_PLACEHOLDER =
'After you have completed each of the 9 Hole Events in your first series, it will be stored here for your records.'

const SeriesList = ({
  series,
  typeCompleted,
  loader,
  searchTerm,
  activePage,
  setActivePage,
  handleSeriesMenuClick,
  removeSeries,
  navigateToEdit,
  navigateToEmails,
  localScores,
}) => {
  const pageCount = useSelector(
    state => typeCompleted ? state.series?.completedSeriesPagination?.totalPages : state.series?.upcomingSeriesPagination?.totalPages
  )
  const [expandedSeriesId, setExpandedSeriesId] = useState(-1)
  const navigate = useNavigate()

  const handleCreateEvent = () => {
    navigate('/series/new')
  }

  useEffect(() => {
    if (!series?.length > 0) {
      return
    }

    setExpandedSeriesId(series[0].id)
  }, [series])

  const expandSeries = (id) => {
    if (expandedSeriesId === id) {
      setExpandedSeriesId(-1)
    } else {
      setExpandedSeriesId(id)
    }
  }

  if (loader) {
    return (
      <div className='col-md-6 mx-auto'>
        <Loader message='loading series' />
      </div>
    )
  }

  if (!series?.length) {
    if (searchTerm) {
      return (
        <div className='mt-5 w-100'>
          <Placeholder
            title={'No Series Results Found'}
            icon={'fa-search'}
            subtext1={'Nothing matched the search criteria'}
            subtext2={'Please try again'}
          />
        </div>
      )
    } else {

      if (typeCompleted) {
        return (
          <CustomPlaceholder
            icon = {<GolferIcon id='icon' fill={colors.lightNavy} width={64} height={64}/>}
            subtext = {COMPLETED_SERIES_PLACEHOLDER}
            title={'No Completed 9 Hole Event Series'}
          />
        )
      }

      return (
        <CustomPlaceholder
          icon = {<GolferIcon id='icon' fill={colors.lightNavy} width={64} height={64}/>}
          subtext = {constants.NINE_HOLE_EVENTS_PLACEHOLDER}
          title={'No 9 Upcoming Events'}
          handleAction = {handleCreateEvent}
          guideText = {'Learn How to Setup an Event Series'}
          buttonText = {'Create Event Series'}
          guideLink={'https://operation36.supporthero.io/article/show/171313'} />
      )
    }
  }

  return (
    <>
      {pageCount > 1 && (
        <Pagination
          pageCount={pageCount}
          activePage={activePage}
          fetchData={(newPage) => setActivePage(newPage)}
        />
      )}
      <div className={pageCount > 1 ? styles.seriesListPaginated : styles.seriesList}>
        {series.map((series, index) => {
          const isExpanded = series.id === expandedSeriesId
          return (
            <div className={classNames('row mx-0', styles.seriesGroup, isExpanded && styles.expanded)} key={series.id}>
              <SeriesRow
                series={series}
                isFirstChild={index === 0}
                handleSeriesMenuClick={handleSeriesMenuClick}
              />
              {series.events.map((e, index) =>
                <SeriesEventRow
                  key={e.id}
                  event={{ ...e, index: index }}
                  inProgress={localScores?.[e.id]?.['scores']}
                />)
              }
              <UncontrolledPopover
                placement='bottom'
                target='seriesMenuButton'
                trigger='legacy'
              >
                <div className={classNames('d-flex flex-column', styles.seriesActions)}>
                  <p aria-hidden='true' onClick={() => navigateToEdit(1)}>Edit Event Series</p>
                  <p aria-hidden='true' onClick={() => navigateToEdit(2)}>Edit Series Schedule</p>
                  <p aria-hidden='true' onClick={() => navigateToEdit(3)}>Edit Master Roster</p>
                  <p aria-hidden='true' onClick={() => navigateToEmails()}>Send an Email</p>
                  <p aria-hidden='true' onClick={() => removeSeries()}>Delete Event Series</p>
                </div>
              </UncontrolledPopover>
            </div>
          )
        }
        )}
      </div>
    </>
  )
}

SeriesList.propTypes = {
  typeCompleted: PropTypes.bool,
  series: PropTypes.array,
  loader: PropTypes.bool,
  searchTerm: PropTypes.string,
  activePage: PropTypes.number,
  setActivePage: PropTypes.func,
  handleSeriesMenuClick: PropTypes.func,
  navigateToEdit: PropTypes.func,
  removeSeries: PropTypes.func,
  navigateToEmails: PropTypes.func,
  localScores: PropTypes.object,
}

export default SeriesList
